import { useNavigate } from "@tanstack/react-router";

import { useAppDispatch } from "@/store/hooks";
import { authSliceActions } from "@/store/slices";

import { authApi } from "./auth.api";
import { LoginDriver, LoginExped } from "./auth.types";

type AuthType = "expedition" | "driver";

type LoginHandlerBody<T extends AuthType> = T extends "expedition" ? LoginExped["args"] : T extends "driver" ? LoginDriver["args"] : never;

function useAuth() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loginDriverTrigger, loginTriggerState] = useLoginDriverMutation();
  const [loginExpedTrigger, loginExpedTriggerState] = useLoginExpedMutation();

  const LoginHandler =
    <TType extends AuthType>(type: TType) =>
    async (body: LoginHandlerBody<TType>) => {
      const matcher = new Map<AuthType, () => Promise<void>>([
        [
          "driver",
          async () => {
            await loginDriverTrigger(body as LoginDriver["args"]).unwrap();
          }
        ],
        [
          "expedition",
          async () => {
            await loginExpedTrigger(body as LoginExped["args"]).unwrap();
          }
        ]
      ]);
      const keys = Array.from(matcher.keys());
      const handler = matcher.get(type);

      if (!handler) return console.error(`The ${type} are not valid! Only are accepted ${keys.join(", ")}!`);

      return await handler();
    };

  const LogoutHandler = (type: AuthType) => {
    const matcher = new Map<AuthType, () => void>([
      ["driver", () => navigate({ to: "/app/login" })],
      ["expedition", () => navigate({ to: "/expedicao/login" })]
    ]);

    const keys = Array.from(matcher.keys());
    const handler = matcher.get(type);

    if (!handler) return console.error(`The ${type} are not valid! Only are accepted ${keys.join(", ")}!`);
    dispatch(authSliceActions.logout());
    handler();
  };

  const SignIn = LoginHandler;
  const SignOut = LogoutHandler;

  return {
    state: {
      signInDriver: loginTriggerState,
      signInExped: loginExpedTriggerState
    },
    SignIn,
    SignOut
  };
}

export const { useLoginDriverMutation, useLoginExpedMutation } = authApi;
export { useAuth };
